import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { FS } from "src/configs/constance";
import NFTCollectionContract from "src/contract-logic/logic/NFTCollectionContract";
import { ERR_TOP_CENTER } from "src/hooks/useNotify";
import { ScoringApiQuery, TravaNFTApiQuery } from "src/utils/query/api-query";

const initState = {
  address: null,
  fetchingStatus: FS.INITIAL,
  creditScore: null,
  transactions: [],
  nonce: 0,
  rank: undefined,
  listNotification: [],
  openNotify: false,
};

export const updateAccInfo = (accountAddress, enqueueSnackbar) => async (dispatch) => {
  dispatch(updateAccountAddress({ accountAddress }));
  dispatch(startFetchAccInfo());
  try {
    dispatch(fetchAccInfoSuccess({ creditScore: null, transactions: [] }));
    return Promise.resolve({ success: true });
  } catch (error) {
    console.error(error);
    dispatch(fetchAccInfoFail());
    error.response && enqueueSnackbar(JSON.stringify(error.response.data), ERR_TOP_CENTER);
    return Promise.reject(error);
  }
};

export const fetchUserRank = createAsyncThunk("accountSlice/fetchUserRank", async (_, thunkAPI) => {
  const accountAddress = thunkAPI.getState().accountSlice.address;
  const res = await ScoringApiQuery.get(`/api-v2/stats/${accountAddress}`);
  const level = res.data.level;
  if (!level) return undefined;
  return String(level).toLowerCase();
});

export const fetchListNotification = createAsyncThunk("accountSlice/fetchListNotification", async (_, thunkAPI) => {
  const accountAddress = thunkAPI.getState().accountSlice.address;
  const res = await TravaNFTApiQuery.get(`/v1/notifications/${accountAddress}`);
  const resBattleHand = await TravaNFTApiQuery.get(`/v1/battles/notifications?address=${accountAddress}`);
  const listNotifyExpedition = res?.expeditions || [];
  const listNotifyVaults = res?.vaults || [];
  const listNotifyTransmutation = res?.transmutations || [];
  const listBattleHand = resBattleHand.roomJoined || [];

  let listTreasureMap = res?.treasureMapTickets || [];
  if (listTreasureMap.length > 0) {
    const metadatas = await Promise.all(
      listTreasureMap.map((i) => NFTCollectionContract.getCollectionMetadata(i.collectionId))
    );
    listTreasureMap = listTreasureMap.map((i, index) => ({ ...i, rarity: metadatas[index].rarity }));
  }

  return [
    ...listNotifyExpedition.map((i) => ({ ...i, type: "expedition" })),
    ...listNotifyVaults.map((i) => ({ ...i, type: "vault" })),
    ...listNotifyTransmutation.map((i) => ({ ...i, type: "transmutation", itemType: i.type })),
    ...listBattleHand.map((i) => ({ ...i, type: "battleHand" })),
    ...listTreasureMap.map((i) => ({ ...i, type: "treasureMap", map: "limited" })),
  ];
});

const accountSlice = createSlice({
  name: "accountSlice",
  initialState: initState,
  reducers: {
    updateAccountAddress: (state, action) => {
      state.address = action.payload.accountAddress;
      // state.address = "0xcf64493616c2debf81be6791260d483864e69b1f"; // test for heuristic framing
      // state.address = "0x2f298db3eda6542ebb5e9c819939b247d1b2bf77";
    },
    startFetchAccInfo: (state) => {
      state.fetchingStatus = FS.FETCHING;
    },
    fetchAccInfoSuccess: (state, action) => {
      Object.assign(state, action.payload);
      state.fetchingStatus = FS.SUCCESS;
    },
    fetchAccInfoFail: (state) => {
      state.fetchingStatus = FS.FAIL;
    },
    resetAccountState: (state) => {
      Object.assign(state, initState);
    },
    updateNonce: (state) => {
      state.nonce = state.nonce + 1;
    },
    setOpenNotify: (state, action) => {
      state.openNotify = action.payload;
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase(fetchUserRank.fulfilled, (state, action) => {
        state.rank = action.payload;
      })
      .addCase(fetchUserRank.rejected, (state, _) => {
        state.rank = undefined;
      })
      .addCase(fetchListNotification.fulfilled, (state, action) => {
        state.listNotification = action.payload;
      })
      .addCase(fetchListNotification.rejected, (state, _) => {
        state.listNotification = [];
      }),
});

export default accountSlice.reducer;
export const {
  updateAccountAddress,
  resetAccountState,
  fetchAccInfoSuccess,
  startFetchAccInfo,
  fetchAccInfoFail,
  updateNonce,
  setOpenNotify,
} = accountSlice.actions;
